import { Injectable, NgZone } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { AuthenticationService } from './authentication.service';

@Injectable({ providedIn: 'root' })
export class StreamService {
  constructor(
    private zone: NgZone,
    private authenticationService: AuthenticationService
  ) {}

  /**
   * Fetches a streaming HTTP response and converts it into an Observable.
   * The response is assumed to be a stream of JSON lines (newline-delimited),
   * where each line begins with `data:` followed by a JSON payload.
   *
   * @template T - The expected type of parsed JSON chunks.
   * @param url - The target endpoint to fetch.
   * @param options - Optional fetch configuration (method, headers, body).
   *                  The body is optional, so this can be used for both
   *                  GET (without body) and POST/PUT (with body) requests.
   * @returns An Observable that emits parsed JSON chunks as they arrive,
   *          or errors if no valid token is available or if parsing fails.
   */
  fetchStream<T>(url: string, options: RequestInit = {}): Observable<T> {
    const subject = new Subject<T>();

    // --- Get and validate token ---
    const getToken = this.authenticationService.getAuthToken();
    if (!getToken || !this.authenticationService.isAuthTokenValide()) {
      this.zone.run(() => subject.error(new Error('No valid authentication token available')));
      return subject.asObservable();
    }
    const token = getToken.access_token;

    // --- Merge Authorization header automatically ---
    const headers = new Headers(options.headers || {});
    headers.set('Authorization', `Bearer ${token}`);

    // Default Content-Type only if not set already
    if (!headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }

    // --- Final fetch options ---
    const finalOptions: RequestInit = { ...options, headers };

    fetch(url, finalOptions)
      .then((response) => {
        const reader = response.body!.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        const readChunk = () => {
          reader.read().then(({ done, value }) => {
            if (done) {
              this.processBuffer<T>(buffer, subject);
              subject.complete();
              return;
            }

            buffer += decoder.decode(value, { stream: true });
            const parts = buffer.split('\n');
            buffer = parts.pop() || '';

            parts.forEach((line) => {
              if (line.trim()) {
                const cleaned = line.replace(/^data:\s*/, '');
                try {
                  const chunk = JSON.parse(cleaned) as T;
                  this.zone.run(() => subject.next(chunk));
                } catch (e) {
                  console.error('Error parsing chunk', e);
                }
              }
            });

            readChunk();
          });
        };

        readChunk();
      })
      .catch((err) => {
        this.zone.run(() => subject.error(err));
      });

    return subject.asObservable();
  }

  /**
   * Processes the final leftover buffer when the stream closes.
   * Attempts to parse it as JSON and emits it if valid.
   */
  private processBuffer<T>(buffer: string, subject: Subject<T>) {
    if (buffer.trim()) {
      try {
        const cleaned = buffer.replace(/^data:\s*/, '');
        const chunk = JSON.parse(cleaned) as T;
        subject.next(chunk);
      } catch (e) {
        console.error('Error processing final chunk', e);
      }
    }
  }
}
